import React, { useState } from "react";

import { useAssumableRoles } from "src/adminPanel/queries";
import { compareRoleNames } from "src/adminPanel/shared/roleCatalog";
import { AssumableRoleRow } from "src/adminPanel/users/AssumableRoleRow";
import { resolveRoleReach } from "src/adminPanel/users/assumableRoleUtils";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { AssumableRole, OrganizationInDB } from "src/clients/taktile-api";
import { Accordion } from "src/design-system/Accordion";
import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { ErrorContent } from "src/router/ErrorPage";
import {
  useTokenFlowFolderScope,
  useTokenRoleScope,
} from "src/store/AuthStore";
import { useSetRoleScopeMutation } from "src/store/authQueries";
import { pluralize } from "src/utils/stringUtils";

const GROUPS = [
  {
    key: "held",
    title: "Roles you hold",
    description: "Granted to you directly.",
    matches: (role: AssumableRole) => role.held_directly,
  },
  {
    key: "derived",
    title: "Roles you can step down to",
    description:
      "Not granted to you directly, but contained in a role you hold — a workspace you can edit is a workspace you can view.",
    matches: (role: AssumableRole) => !role.held_directly,
  },
] as const;

const FLOW_FOLDER_ARG = "flow_folder_id";

export const TokenImpersonationModal: React.FC<{
  isOpen: boolean;
  organizations: OrganizationInDB[];
  workspaces: WorkspaceDataplane[];
  onClose: () => void;
}> = ({ isOpen, organizations, workspaces, onClose }) => {
  const activeScope = useTokenRoleScope();
  const activeFolders = useTokenFlowFolderScope();
  const { mutate: setRoleScope, isPending: isMinting } =
    useSetRoleScopeMutation();
  // Only fetched while the modal is open: the walk is cheap but it is a role
  // graph, not something the users tab needs before you ask for it.
  const assumableRoles = useAssumableRoles(isOpen);

  const [selected, setSelected] = useState<string[]>(activeScope ?? []);
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>(
    activeFolders ?? [],
  );
  const [wasOpen, setWasOpen] = useState(isOpen);

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setSelected(activeScope ?? []);
      setSelectedFolderIds(activeFolders ?? []);
    }
  }

  const reachByRole = new Map(
    (assumableRoles.data ?? []).map((role) => [
      role.name,
      resolveRoleReach(role, workspaces, organizations),
    ]),
  );

  const takesFolders = (roleNames: string[]) =>
    (assumableRoles.data ?? []).some(
      (role) =>
        roleNames.includes(role.name) &&
        role.arg_names.includes(FLOW_FOLDER_ARG),
    );

  const takesFlowFolder = takesFolders(selected);

  const handleToggle = (name: string) => {
    const next = selected.includes(name)
      ? selected.filter((role) => role !== name)
      : [...selected, name];
    setSelected(next);
    // Folder picks are meaningless without a folder-dimensioned role selected;
    // clearing them keeps the checkboxes honest about what the mint sends.
    if (selectedFolderIds.length > 0 && !takesFolders(next)) {
      setSelectedFolderIds([]);
    }
  };

  const handleToggleFolder = (
    roleName: string,
    folderId: string,
    offeredFolderIds: string[],
  ) => {
    const deselectRole = () =>
      setSelected((current) => current.filter((role) => role !== roleName));

    // Unticking a folder while the role rides the wildcard materializes the
    // workspace's remaining folders as the explicit selection; unticking the
    // only folder leaves nothing to keep, so the role goes with it.
    if (selected.includes(roleName) && selectedFolderIds.length === 0) {
      const remaining = offeredFolderIds.filter((id) => id !== folderId);
      setSelectedFolderIds(remaining);
      if (remaining.length === 0) {
        deselectRole();
      }
      return;
    }
    const isAdding = !selectedFolderIds.includes(folderId);
    const next = isAdding
      ? [...selectedFolderIds, folderId]
      : selectedFolderIds.filter((id) => id !== folderId);
    // Re-completing the full folder list is the wildcard again, but only for
    // a single-workspace role: on a multi-workspace one the wildcard would
    // also re-include every other workspace's folders, which is wider than
    // what the pickers show.
    const isWholeRoleAgain =
      isAdding &&
      (reachByRole.get(roleName)?.workspaces.length ?? 0) === 1 &&
      offeredFolderIds.every((id) => next.includes(id)) &&
      next.every((id) => offeredFolderIds.includes(id));
    setSelectedFolderIds(isWholeRoleAgain ? [] : next);
    // Narrowing to a folder implies wanting the role itself; unticking the
    // last picked folder retracts it, instead of falling back to all folders.
    if (isAdding && !selected.includes(roleName)) {
      setSelected((current) => [...current, roleName]);
    }
    if (!isAdding && next.length === 0) {
      deselectRole();
    }
  };

  const handleMint = () =>
    setRoleScope(
      {
        roles: selected,
        flowFolderIds:
          takesFlowFolder && selectedFolderIds.length > 0
            ? selectedFolderIds
            : undefined,
      },
      {
        onError: () =>
          toastActions.failure({
            title: "Could not mint the token",
            description:
              "The impersonation was rejected. Your session is unchanged.",
          }),
      },
    );

  return (
    <Modal open={isOpen} size="md" onClose={onClose}>
      <Modal.Header description="Mint a session token that carries only the roles you select. The whole application — UI and API alike — sees just those roles until you remove the impersonation or log out.">
        Token impersonation
      </Modal.Header>
      <Modal.Content>
        {assumableRoles.isPending ? (
          <Skeleton height="h-5" rounded="rounded-lg" />
        ) : assumableRoles.isError ? (
          <ErrorContent
            message={assumableRoles.error?.message ?? "Please try again later."}
          />
        ) : (
          <div className="flex flex-col gap-4">
            {GROUPS.map((group) => {
              const inGroup = assumableRoles
                .data!.filter(group.matches)
                .sort((a, b) => compareRoleNames(a.name, b.name));
              return inGroup.length === 0 ? null : (
                <div key={group.key}>
                  <p className="font-inter-medium-12px text-gray-800">
                    {group.title}
                  </p>
                  <p className="mb-1.5 font-inter-normal-12px text-gray-500">
                    {group.description}
                  </p>
                  <Accordion dataLoc={`token-impersonation-roles-${group.key}`}>
                    {inGroup.map((role) => (
                      <AssumableRoleRow
                        key={role.name}
                        isDisabled={isMinting}
                        isSelected={selected.includes(role.name)}
                        reach={reachByRole.get(role.name)!}
                        folderSelection={{
                          selectedFolderIds,
                          onToggleFolder: (folderId, offeredFolderIds) =>
                            handleToggleFolder(
                              role.name,
                              folderId,
                              offeredFolderIds,
                            ),
                        }}
                        role={role}
                        onToggle={() => handleToggle(role.name)}
                      />
                    ))}
                  </Accordion>
                </div>
              );
            })}
            {takesFlowFolder && (
              <div>
                <p className="font-inter-medium-12px text-gray-800">
                  Flow folders
                </p>
                <p
                  className="font-inter-normal-12px text-gray-500"
                  data-loc="token-impersonation-folder-summary"
                >
                  {selectedFolderIds.length > 0
                    ? `The selected flow roles are restricted to ${pluralize(
                        selectedFolderIds.length,
                        "folder",
                      )}. Pick them under each workspace above.`
                    : "Pick folders under each workspace above to restrict the selected flow roles. Nothing picked keeps every folder they already reach."}
                </p>
              </div>
            )}
            <p className="font-inter-normal-12px text-gray-500">
              {selected.length} of {assumableRoles.data!.length} roles selected
            </p>
          </div>
        )}
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="token-impersonation-mint"
            isDisabled={selected.length === 0 || isMinting}
            isLoading={isMinting}
            onClick={handleMint}
          >
            Mint token
          </Button>
        }
      />
    </Modal>
  );
};
